import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Star } from 'lucide-react';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../../ui/sheet';
import { Avatar, AvatarFallback, AvatarImage } from '../../ui/avatar';
import { apiClient } from '../../../utils/api-client';

interface RaterRow {
  ascentLogId: string;
  username: string;
  slug: string;
  avatarUrl: string | null;
  rating: number;
}

interface ClimbRatersDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  climbSlugOrId: string;
}

interface RawSendItem {
  attributes: {
    ascent_log_id?: string;
    rating?: number | null;
    user: {
      username: string;
      slug: string;
      avatar_small_url?: string | null;
    };
  };
}

function StarRow({ rating }: { rating: number }) {
  return (
    <div className="flex items-center gap-0.5">
      {[ 1, 2, 3, 4, 5 ].map(n => (
        <Star
          key={n}
          className="h-3.5 w-3.5"
          fill={n <= Math.round(rating) ? 'currentColor' : 'none'}
        />
      ))}
    </div>
  );
}

export function ClimbRatersDrawer({ open, onOpenChange, climbSlugOrId }: ClimbRatersDrawerProps) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState<RaterRow[]>([]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    apiClient
      .get(`/api/climbs/${climbSlugOrId}/sends`)
      .then(res => res.json())
      .then((json: { data: RawSendItem[] }) => {
        if (cancelled) return;
        const mapped: RaterRow[] = (json.data ?? [])
          .filter(item => item.attributes.rating != null)
          .map(item => ({
            ascentLogId: item.attributes.ascent_log_id ?? '',
            username: item.attributes.user.username,
            slug: item.attributes.user.slug,
            avatarUrl: item.attributes.user.avatar_small_url ?? null,
            rating: item.attributes.rating as number,
          }))
          .sort((a, b) => b.rating - a.rating);
        setRows(mapped);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, climbSlugOrId]);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="max-h-[80vh] overflow-y-auto">
        <SheetHeader>
          <SheetTitle>{t('home.instagram.ratersTitle')}</SheetTitle>
        </SheetHeader>
        <div className="mt-4 space-y-2">
          {loading ? (
            <div className="text-sm text-muted-foreground py-6 text-center">…</div>
          ) : rows.length === 0 ? (
            <div className="text-sm text-muted-foreground py-6 text-center">
              {t('home.instagram.noRaters')}
            </div>
          ) : (
            rows.map(row => (
              <Link
                key={row.ascentLogId}
                to={`/users/${row.slug}`}
                className="flex items-center gap-3 p-2 rounded-md hover:bg-muted transition-colors"
              >
                <Avatar className="h-9 w-9">
                  {row.avatarUrl ? <AvatarImage src={row.avatarUrl} alt={row.username} /> : null}
                  <AvatarFallback className="text-xs">
                    {row.username.charAt(0).toUpperCase()}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">{row.username}</div>
                </div>
                <StarRow rating={row.rating} />
              </Link>
            ))
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
